import { Outlet } from 'react-router-dom';
import { useParams } from 'react-router';
import { useGetUserExternalDevicesQuery, useGetUserAnnotationsQuery, useGetUserLastValuesQuery, useGetUserNotificationsQuery, useGetUsersQuery } from 'features/api/apiSlice';

// material-ui
import { Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import { ReconciliationOutlined, CommentOutlined, BellOutlined, LineChartOutlined } from '@ant-design/icons';
import React from 'react';
import moment from 'moment';
import AnnotationsTab from 'pages/patients/tabs/AnnotationsTab';
import NotificationsTab from 'pages/patients/tabs/NotificationsTab';
import PatientHealthData from 'pages/patients/CHAMP/tabs/PatientHealthData';
import { useTranslation } from 'react-i18next';
import EmptyUserCard from 'components/cards/skeleton/EmptyUserCard';
import { PatientChartTab } from 'pages/patients/tabs/PatientChartTab';
import { selectCurrentUser } from 'features/auth/authSlice';
import { useAppSelector } from 'app/hooks';
  
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

// ==============================|| CUSTOMER - CARD PREVIEW ||============================== //
  
export default function PatientFileDialog () {
  const { t } = useTranslation();
  const authUser = useAppSelector(selectCurrentUser);
  const { userid } = useParams();
  
  const { data: users, isLoading: isLoadingUsers, isFetching: isFetchingUsers } = useGetUsersQuery({ parentId: authUser!!.id!!, role: null })

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  
  const { data: notifications, isLoading: isLoadingNotifications, isFetching: isFetchingNotifications } = useGetUserNotificationsQuery({
    userId: userid!!,
    from: null,
    to: moment.utc().endOf("day").toDate().toISOString(),
    readByClinician: null
  })

  const { data: annotations, isLoading: isLoadingAnnotations, isFetching: isFetchingAnnotations } = useGetUserAnnotationsQuery({
    userId: userid!!,
    from: null,
    to: moment.utc().endOf("day").toDate().toISOString()
  })

  const { data: devicesDataExtended, isLoading: isLoadingDeviceDataExtended, isFetching: isFetchingDeviceDataExtended } = useGetUserLastValuesQuery(userid!!)

  const { data: externalDevices, isLoading: isLoadingExternalDevices, isFetching: isFetchingExternalDevices } = useGetUserExternalDevicesQuery(userid!!)

  if (isLoadingAnnotations || isLoadingDeviceDataExtended || isLoadingNotifications || isLoadingUsers || isLoadingExternalDevices) {
    return (
      <Grid container spacing={3}>
          <EmptyUserCard title={t('loading', 'Loading...')} />
      </Grid>
    )
  } else if (isFetchingAnnotations || isFetchingDeviceDataExtended || isFetchingNotifications || isFetchingUsers || isFetchingExternalDevices) {
    return (
      <Grid container spacing={3}>
          <EmptyUserCard title={t('fetching', 'Fetching...')} />
      </Grid>
    )
  } else {
    const patient = users!!.find(x => x.id == userid)

    return (
      <>
        <Box sx={{ width: '100%' }}>
          <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons="auto" aria-label="account profile tab">
            <Tab label={t('health_data', 'Health data')} icon={<ReconciliationOutlined />} iconPosition="start" sx={{textTransform: 'none'}} {...a11yProps(0)}/>
            <Tab label={t('annotations', 'Annotations')} icon={<CommentOutlined />} iconPosition="start" sx={{textTransform: 'none'}} {...a11yProps(1)}/>
            <Tab label={t('notifications', 'Notifications')} icon={<BellOutlined />} iconPosition="start" sx={{textTransform: 'none'}} {...a11yProps(2)}/>
            <Tab label={t('charts', 'Charts')} icon={<LineChartOutlined />} iconPosition="start" sx={{textTransform: 'none'}} {...a11yProps(3)}/>
          </Tabs>
        </Box>
        <Box sx={{ mt: 2.5 }}>
          <CustomTabPanel value={value} index={0}>
            <PatientHealthData devicesDataExtended={devicesDataExtended!!}/>
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <AnnotationsTab annotations={annotations!!} users={users!!.concat(authUser!!)} isLoading={isLoadingAnnotations} isFetching={isFetchingAnnotations} />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <NotificationsTab notifications={notifications!!} users={[patient!!]} isSingleUser={true} isLoading={isLoadingNotifications} isFetching={isFetchingNotifications} />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={3}>
            <PatientChartTab
              patientId={patient!!.id!!}
              users={users!!.concat(authUser!!)}
              externalDevices={externalDevices!!}
              annotations={annotations ?? []}
            />
          </CustomTabPanel>
          <Box sx={{ mt: 2.5 }}>
            <Outlet />
          </Box>
        </Box>
      </>
    )
  }
}
  